import Bowser from "bowser";
import moment from "moment";
import { SyncRedactor } from "redact-pii-light";
import conditionsEngine from "./conditionsEngine";
import {
  builtInRedactorList,
  commonwWordNameRegex,
  customDigitsRegex,
  customRedactorList,
  nameRegex,
  usCityRegex,
  usStateAbbrevsRegex,
  usStateRegex,
} from "./piiRedaction";
import { MM_PRIVACY_URLS } from "../constants";

export * from "./pubSub";

export { conditionsEngine };

/**
 * Parses out the conditions for displaying a survey question
 *  - lifted from Bill Feng
 *
 *  @param {String} conditions - a string of conditions
 *  @param {FormData} formData - form data object containing form data
 *
 *  @return {Boolean}
 */
export const parseSurveyCondition = ({ conditions }, formData) => {
  if (!conditions) return true;
  if (conditions === "") return true;

  // '|' is for 'or', '&' is for 'and'
  // a conditions may contain | or &, but not both
  // e.g. material=married|material=engaged
  // e.g. dependents.count>0 & material=married
  let cases = [];
  let tokens;
  switch (true) {
    case conditions.indexOf("|") >= 0: {
      // logic for 'or'
      cases = conditions.split("|");
      return cases.some((x) => {
        switch (true) {
          case x.indexOf("!=") >= 0: {
            tokens = x.split("!=");
            return formData.get(tokens[0]) !== tokens[1];
          }
          case x.indexOf("==") >= 0: {
            tokens = x.split("==");
            return formData.get(tokens[0]) === tokens[1];
          }
          case x.indexOf(">") >= 0: {
            tokens = x.split(">");
            return formData.get(tokens[0]) >= tokens[1];
          }
          case x.indexOf("<") >= 0: {
            tokens = x.split("<");
            return formData.get(tokens[0]) <= tokens[1];
          }
          default:
        }
      });
    }

    default:
      // logic for 'and', or single conditions (contains neither \ nor &)
      cases = conditions.split("&");
      return cases.every((x) => {
        switch (true) {
          case x.indexOf("!=") >= 0: {
            tokens = x.split("!=");
            return formData.get(tokens[0]) !== tokens[1];
          }
          case x.indexOf("==") >= 0: {
            tokens = x.split("==");
            return formData.get(tokens[0]) === tokens[1];
          }
          case x.indexOf(">") >= 0: {
            tokens = x.split(">");
            return formData.get(tokens[0]) >= tokens[1];
          }
          case x.indexOf("<") >= 0: {
            tokens = x.split("<");
            return formData.get(tokens[0]) <= tokens[1];
          }
          default:
        }
      });
  }
};

/**
 * Parses out the conditions for displaying a one panel survey question
 *  - lifted from Bill Feng
 *
 *  @param {String} conditions - a string of conditions
 *  @param {FormData} formData - form data object containing form data
 *
 *  @return {Boolean}
 */
export const parseOnePanelSurveyCondition = ({ conditions }, formData) => {
  if (!conditions) return true;
  if (conditions === "") return true;

  // '|' is for 'or', '&' is for 'and'
  // a conditions may contain | or &, but not both
  // e.g. material=married|material=engaged
  // e.g. dependents.count>0 & material=married
  let cases = [];
  let tokens;
  switch (true) {
    case conditions.indexOf("|") >= 0: {
      // logic for 'or'
      cases = conditions.split("|");
      return cases.some((x) => {
        switch (true) {
          case x.indexOf("!=") >= 0: {
            tokens = x.split("!=");
            return formData.get(tokens[0]) !== tokens[1];
          }
          case x.indexOf("==") >= 0: {
            tokens = x.split("==");
            return formData.get(tokens[0]) === tokens[1];
          }
          case x.indexOf(">") >= 0: {
            tokens = x.split(">");
            return formData.get(tokens[0]) >= tokens[1];
          }
          case x.indexOf("<") >= 0: {
            tokens = x.split("<");
            return formData.get(tokens[0]) <= tokens[1];
          }
          default:
        }
      });
    }

    default:
      // logic for 'and', or single conditions (contains neither \ nor &)
      cases = conditions.split("&");
      return cases.every((x) => {
        switch (true) {
          case x.indexOf("!=") >= 0: {
            tokens = x.split("!=");
            return formData.get(tokens[0]) !== tokens[1];
          }
          case x.indexOf("==") >= 0: {
            tokens = x.split("==");
            return formData.get(tokens[0]) === tokens[1];
          }
          case x.indexOf(">") >= 0: {
            tokens = x.split(">");
            return formData.get(tokens[0]) >= tokens[1];
          }
          case x.indexOf("<") >= 0: {
            tokens = x.split("<");
            return formData.get(tokens[0]) <= tokens[1];
          }
          default:
        }
      });
  }
};

/**
 * Gets information about the client
 *
 *  @return {Object} - contains information about client
 */
export const getClientInformation = () => {
  return {
    ...Bowser.parse(window.navigator.userAgent),
    viewport: {
      vh: Math.round(
        Math.max(
          document.documentElement.clientHeight || 0,
          window.innerHeight || 0
        )
      ),
      vw: Math.round(
        Math.max(
          document.documentElement.clientWidth || 0,
          window.innerWidth || 0
        )
      ),
    },
  };
};

/**
 * Checks if today is in the survey/popup date range
 *
 *  @param {dateRangeObj} - object containing beginDate and endDate
 *
 *  @return {Boolean}
 */
export const checkInDateRange = (dateRangeObj) => {
  const todaysDate = moment().format("YYYY-MM-DD");
  const { beginDate, endDate } = dateRangeObj;
  const formatBeginDate = moment(beginDate, "MM/DD/YYYY").format("YYYY-MM-DD");
  const formatEndDate = moment(endDate, "MM/DD/YYYY").format("YYYY-MM-DD");

  let isInRange = moment(todaysDate).isBetween(
    formatBeginDate,
    formatEndDate,
    "day", // day comparison (e.g. is 2021-11-23 in between 2021-11-22 and 2021-11-24 regardless of time)
    "[]" // Sets the range to inclusive of both the beginDate and endDate
  );

  return isInRange;
};

export const getUrl = (url) => {
  let windowLocationObj = url;
  const urlhref = url.href;
  const pathname = url.pathname;
  const queryString = url.search;

  const cleanUrl = urlRegexTest(urlhref);
  const cleanPathname = urlRegexTest(pathname);
  const cleanQueryString = urlRegexTest(queryString);

  windowLocationObj = {
    ...windowLocationObj,
    href: cleanUrl,
    pathname: cleanPathname,
    search: cleanQueryString,
  };

  return windowLocationObj;
};

const urlRegexTest = (value) => {
  const expressions = MM_PRIVACY_URLS,
    len = expressions.length;
  let i = 0,
    splitvalue = "",
    regexLastIndex = false;

  for (; i < len; i++) {
    let regexTest = new RegExp(expressions[i].toLowerCase(), "g");
    if (regexTest.test(value.toLowerCase())) {
      regexLastIndex = regexTest.lastIndex;
      splitvalue = value.slice(0, regexLastIndex);
    }
  }

  return splitvalue ? splitvalue : value;
};

/**
 * Removes PII from all survey response values
 * @param {String} surveyResponse
 *
 * @return {String} result
 */
const redactResponsePii = (surveyResponse) => {
  // Handle us cities, us statews, well known names first
  let cleanResult = surveyResponse.replace(usCityRegex, " US_CITY");
  cleanResult = cleanResult.replace(usStateAbbrevsRegex, " US_STATE");
  cleanResult = cleanResult.replace(usStateRegex, " US_STATE");
  cleanResult = cleanResult.replace(commonwWordNameRegex, " PERSONS_NAME");
  cleanResult = cleanResult.replace(nameRegex, " PERSONS_NAME");

  // Redact Pii Light starts its work here
  const redactor = new SyncRedactor({
    builtInRedactors: builtInRedactorList,
    customRedactors: customRedactorList,
  });

  const redactPIIResult = redactor.redact(cleanResult);

  const result = redactPIIResult.replace(customDigitsRegex, "DIGITS");

  return result;
};

/**
 * Prepares the survey data for the payload
 *
 * @param {String} surveyTitle
 * @param {Object} surveyPayload
 *
 * @return {Object} result
 */
export const prepareSurveyPayload = (
  surveyTitle,
  surveyPayload,
  isPIIRedactionEnabled = true
) => {
  const result = { title: surveyTitle, totalQuestions: 0, data: [] };

  Object.keys(surveyPayload).forEach((key, index) => {
    result.data.push({
      question: key,
      value: isPIIRedactionEnabled
        ? redactResponsePii(surveyPayload[key])
        : surveyPayload[key],
      index: index + 1,
    });

    result.totalQuestions++;
  });

  return result;
};

/**
 * Prepares the onePanelSurvey data for the payload
 *
 * @param {String} onePanelSurveyTitle
 * @param {Object} onePanelSurveyPayload
 *
 * @return {Object} result
 */
export const prepareOnePanelSurveyPayload = (
  onePanelSurveyTitle,
  onePanelSurveyPayload
) => {
  const result = { title: onePanelSurveyTitle, totalQuestions: 0, data: [] };

  Object.keys(onePanelSurveyPayload).forEach((key, index) => {
    result.data.push({
      question: key,
      value: redactResponsePii(onePanelSurveyPayload[key]),
      index: index + 1,
    });

    result.totalQuestions++;
  });

  return result;
};

/**
 * Gets snowplow info
 *
 * @param {String | null} cookieName - name of cookie to look for
 *
 * @return {Object | boolean} result
 */
/* istanbul ignore next */
export const getSnowplowInfo = (cookieName) => {
  const match = getSnowplowCookie(cookieName);
  if (match && match[1]) {
    console.log("Balto:: Cookie Exists");
    const splitStr = match[1].split(".");

    if (splitStr.length > 0) {
      return {
        sid: splitStr[splitStr.length - 1],
        duid: splitStr[0],
      };
    } else {
      return false;
    }
  } else {
    console.log("Balto:: Cookie Does Not Exist");
    return false;
  }
};

/**
 * get snowplow cookie info
 * @returns {RegExpMatchArray}
 */
export const getSnowplowCookie = (cookieName) => {
  cookieName = cookieName || "_sp_";
  const matcher = new RegExp(cookieName + "id\\.[a-f0-9]+=([^;]+);?");
  return document.cookie.match(matcher);
};

/**
 * Gets the quantum metrics replay url
 *
 * @return {String}
 */
/* istanbul ignore next */
export const getQuantumMetricsReplayUrl = () => {
  return window.QuantumMetricAPI ? window.QuantumMetricAPI.getReplay() : "";
};

export const QuantumMetricsOnRageEvent = (evt) => {
  console.log("BaltoQMRage: " + JSON.stringify(evt));
  const rageDiv = document.createElement("div");
  rageDiv.setAttribute("id", "balto-rage-event");
  document.body.appendChild(rageDiv);
};

/**
 * Delays the execution for a set amount of time, can only be used async
 * @param {Number} - delay
 * @return {Promise}
 */
export const sleep = (delay) =>
  new Promise((resolve) => setTimeout(resolve, delay));

/**
 * Gets the text content from the screen
 * @param {String | Null} - selector
 * @return {String | Null}
 */
export const getScreenContent = (selector) => {
  if (!selector) return null;

  const element = document.querySelector(selector)?.textContent;

  return element ? element : null;
};
